import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Grid,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  IconButton
} from '@mui/material';
import {
  LocationOn,
  Work,
  CalendarToday,
  Business,
  Send,
  Upload,
  Email,
  Person,
  Phone,
  Close,
  ArrowBack
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { parseResumeWithAI, matchResumeWithJD } from '../../services/aiResumeParserService';
import { getAuthUser } from '../../services/authService';


interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  is_remote: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  requirements: string[];
  benefits: string[];
  responsibilities?: string[];
  expiry_date: string;
  status: string;
  entity_id: string;
  created_at: string;
  views_count: number;
  applications_count: number;
}

interface Entity {
  id: string;
  name: string;
  logo?: string;
}

interface ApplicationForm {
  applicant_name: string;
  applicant_email: string;
  applicant_phone: string;
  cover_letter: string;
  resume: File | null;
}

export const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();

  const [job, setJob] = useState<Job | null>(null);
  const [entity, setEntity] = useState<Entity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [parseProgress, setParseProgress] = useState('');

  const [formData, setFormData] = useState<ApplicationForm>({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    cover_letter: '',
    resume: null
  });

  useEffect(() => {
    if (id) {
      fetchJobDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is logged in (admin/hr viewing)
      const currentUser = await getAuthUser();
      const isAdminView = currentUser && ['admin', 'hr', 'manager', 'recruiter'].includes(currentUser.role || '');

      // Fetch job details - admins can view all jobs, public users only see active public jobs
      let query = supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null);

      // Only apply public/active filters for non-admin users
      if (!isAdminView) {
        query = query.eq('is_public', true).eq('status', 'active');
      }

      const { data: jobData, error: jobError } = await query.single();

      if (jobError) {
        throw jobError;
      }

      if (!jobData) {
        setError('Job not found or no longer available');
        return;
      }

      // Check if job is expired (skip for admin users)
      const expiryDate = new Date(jobData.expiry_date);
      if (!isAdminView && expiryDate < new Date()) {
        setError('This job posting has expired');
        return;
      }

      setJob(jobData);

      // Fetch entity details
      const { data: entityData, error: entityError } = await supabase
        .from('entities')
        .select('id, name, logo')
        .eq('id', jobData.entity_id)
        .single();

      if (!entityError && entityData) {
        setEntity(entityData);
      }

      // Increment view count
      await supabase
        .from('jobs')
        .update({ views_count: (jobData.views_count || 0) + 1 })
        .eq('id', id);

    } catch (err: any) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError('Please upload a PDF or Word document');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError('File size must be less than 5MB');
        return;
      }

      setFormData(prev => ({ ...prev, resume: file }));
      setSubmitError(null);
    }
  };

  const handleSubmitApplication = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      // Validate form
      if (!formData.applicant_name || !formData.applicant_email || !formData.resume) {
        setSubmitError('Please fill in all required fields');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.applicant_email)) {
        setSubmitError('Please enter a valid email address');
        return;
      }

      // Check if already applied
      const { data: existingApplication } = await supabase
        .from('applications')
        .select('id')
        .eq('job_id', id)
        .eq('applicant_email', formData.applicant_email)
        .single();

      if (existingApplication) {
        setSubmitError('You have already applied for this position');
        return;
      }

      // Parse resume and run AI analysis
      // Using the SAME flow as bulk upload for consistent scoring
      setParseProgress('Analyzing resume...');
      let aiAnalysisResults = null;
      let aiParsedResume = null;

      try {
        // Step 1: Parse resume with AI (same as bulk upload - uses /api/parse-resume)
        setParseProgress('Analyzing resume...');
        aiParsedResume = await parseResumeWithAI(formData.resume);

        // Step 2: Match with job description (same as bulk upload - uses /api/match-resume)
        setParseProgress('Analyzing resume...');
        aiAnalysisResults = await matchResumeWithJD(aiParsedResume, {
          title: job?.title || '',
          description: job?.description || '',
          requirements: job?.requirements || [],
          benefits: job?.benefits || [],
          responsibilities: job?.responsibilities || [],
          location: job?.location || '',
          workType: job?.is_remote ? 'remote' : 'onsite'
        });

        console.log('[Application] AI analysis completed - Score:', aiAnalysisResults?.overallScore);
      } catch (aiError: any) {
        console.warn('[Application] AI analysis failed:', aiError.message);
        // Continue without AI results - application can still be submitted
        // Score will be null, which is acceptable
      }

      // Upload resume to storage
      setParseProgress('Uploading resume...');
      const fileExt = formData.resume.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `resumes/${job?.entity_id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('entity-assets')
        .upload(filePath, formData.resume);

      if (uploadError) {
        throw new Error('Failed to upload resume');
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('entity-assets')
        .getPublicUrl(filePath);

      // Create application
      // Prepare screening results with parsed data (same structure as bulk upload)
      const screeningResults = aiParsedResume ? {
        parsedResume: aiParsedResume,
        matchResult: aiAnalysisResults,
        skills: aiParsedResume.skills || [],
        experienceYears: aiParsedResume.yearsOfExperience || 0,
        education: aiParsedResume.education || [],
        resumeText: aiParsedResume.extractedText || '',
        processedAt: new Date().toISOString()
      } : null;

      setParseProgress('Submitting application...');
      const { error: applicationError } = await supabase
        .from('applications')
        .insert({
          job_id: id,
          entity_id: job?.entity_id,
          applicant_name: formData.applicant_name,
          applicant_email: formData.applicant_email,
          applicant_phone: formData.applicant_phone || null,
          resume_url: publicUrl,
          cover_letter: formData.cover_letter || null,
          status: 'reviewing',
          match_score: aiAnalysisResults?.overallScore || null,
          screening_results: screeningResults,
          ai_analysis_results: aiAnalysisResults
        });

      if (applicationError) {
        throw applicationError;
      }

      // Update application count
      if (job) {
        await supabase
          .from('jobs')
          .update({ applications_count: (job.applications_count || 0) + 1 })
          .eq('id', id);
      }

      setSubmitSuccess(true);
      setApplyDialogOpen(false);

      // Reset form
      setFormData({
        applicant_name: '',
        applicant_email: '',
        applicant_phone: '',
        cover_letter: '',
        resume: null
      });

    } catch (err: any) {
      console.error('Error submitting application:', err);
      setSubmitError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
      setParseProgress('');
    }
  };

  const formatSalary = (min: number | null, max: number | null) => {
    if (!min || !max) return 'Competitive';
    //return `${job?.salary_currency || ''} ${min.toLocaleString()} - ${max.toLocaleString()}`;
      return `${job!.salary_currency} ${min.toLocaleString()} - ${max.toLocaleString()}`; 
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !job) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Job not found'}
        </Alert>
        <Button
          component={Link}
          to="/jobs"
          startIcon={<ArrowBack />}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Back to Jobs
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        component={Link}
        to="/jobs"
        startIcon={<ArrowBack />}
        sx={{ mb: 3 }}
      >
        Back to Jobs
      </Button>

      {/* Success Message */}
      {submitSuccess && (
        <Alert
          severity="success"
          onClose={() => setSubmitSuccess(false)}
          sx={{ mb: 3 }}
        >
          Application submitted successfully! We'll review your application and get back to you soon.
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            {/* Company Info */}
            {entity && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                {entity.logo && (
                  <Box
                    component="img"
                    src={entity.logo}
                    alt={entity.name}
                    sx={{ height: 50, width: 50, objectFit: 'contain' }}
                  />
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {entity.name}
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* Job Title */}
            <Typography variant="h3" fontWeight={700} gutterBottom>
              {job.title}
            </Typography>

            {/* Job Meta Info */}
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
              <Chip
                icon={<LocationOn />}
                label={job.is_remote ? `${job.location} (Remote)` : job.location}
                variant="outlined"
              />
              <Chip
                
                label={formatSalary(job.salary_min, job.salary_max)}
                variant="outlined"
              />
              <Chip
                icon={<CalendarToday />}
                label={`Expires ${formatDate(job.expiry_date)}`}
                variant="outlined"
              />
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Job Description */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                About the Role
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {job.description}
              </Typography>
            </Box>

          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              position: 'sticky',
              top: 20,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<Send />}
                onClick={() => setApplyDialogOpen(true)}
                sx={{
                  py: 1.5,
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                }}
              >
                Apply Now
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Job Overview
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Business fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Company
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500}>
                    {entity?.name || 'Not specified'}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500}>
                    {job.location}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Work fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Job Type
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500}>
                    {job.is_remote ? 'Remote' : 'On-site'}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                   
                    <Typography variant="body2" color="text.secondary">
                      Salary
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500}>
                    {formatSalary(job.salary_min, job.salary_max)}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Posted
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(job.created_at)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Application Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={() => !submitting && setApplyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Apply for {job.title}
            </Typography>
            <IconButton onClick={() => setApplyDialogOpen(false)} disabled={submitting}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label="Full Name"
              name="applicant_name"
              value={formData.applicant_name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              name="applicant_email"
              value={formData.applicant_email}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label="Phone Number"
              name="applicant_phone"
              value={formData.applicant_phone}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Upload />}
                sx={{ py: 1.5 }}
              >
                {formData.resume ? formData.resume.name : 'Upload Resume (Required)'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                PDF or Word document, max 5MB
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Cover Letter (Optional)"
              name="cover_letter"
              value={formData.cover_letter}
              onChange={handleInputChange}
              placeholder="Tell us why you're a great fit for this role..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setApplyDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitApplication}
            disabled={submitting || !formData.resume}
            startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
          >
            {submitting ? (parseProgress || 'Submitting...') : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
